import dayjs from "dayjs"
import isBetween from "dayjs/plugin/isBetween"

dayjs.extend(isBetween)

const WEEKDAY_INDEX = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 }

// Whether a guest can't start a booking of `trip` (an embedded experience detail) on `date`: past
// days, days after the guide's scheduled account deletion, blocked weekdays, unavailable or
// sold-out dates, and days outside the trip's available date ranges (when it has any).
export const isExperienceDateUnavailable = (trip, date) => {
  if (!date || !trip) return true

  const day = dayjs(date)
  const today = dayjs().startOf("day")
  const scheduledDeletionDate = trip?.guide?.scheduled_deletion_date
  const blockedWeekdays = trip?.unavailable_weekdays?.map((weekday) => WEEKDAY_INDEX[weekday.toLowerCase()]) || []
  const isSoldOut = trip?.sold_out_dates?.some((dateStr) => day.isSame(dayjs(dateStr), "day"))
  const isUnavailable = trip?.unavailable_dates?.some((dateStr) => day.isSame(dayjs(dateStr), "day"))
  const hasDateRanges = Array.isArray(trip?.available_date_ranges) && trip.available_date_ranges.length > 0
  const isInAvailableRange =
    !hasDateRanges ||
    trip.available_date_ranges.some((range) => day.isBetween(dayjs(range.start).startOf("day"), dayjs(range.end).endOf("day"), null, "[]"))

  return (
    day.isBefore(today) ||
    // dayjs().isAfter(undefined) compares with now, so only check when a deletion date is set.
    (!!scheduledDeletionDate && day.isAfter(scheduledDeletionDate)) ||
    blockedWeekdays.includes(day.day()) ||
    !!isUnavailable ||
    !!isSoldOut ||
    !isInAvailableRange
  )
}

// available_slots returns plain strings for anonymous callers but { slot, available } objects for
// authenticated ones (same normalization as the pro BookingForm) — keep only available slot strings.
export const normalizeTimeSlots = (slots) =>
  (Array.isArray(slots) ? slots : [])
    .filter((item) => typeof item === "string" || item?.available !== false)
    .map((item) => (typeof item === "string" ? item : item?.slot))
    .filter(Boolean)

// "8:00 AM - 10:00 AM" -> "08:00 AM" (the start of a single-day slot, as the design shows it).
export const getSlotStartLabel = (slot) => {
  const start = String(slot || "").split(" - ")[0].trim()
  const match = start.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  return match ? `${match[1].padStart(2, "0")}:${match[2]} ${match[3].toUpperCase()}` : start
}

const SCHEDULE_DAY_INDEX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 }

// Number of calendar days a multi-day experience spans (duration_value is in days for multi-day).
export const getMultiDaySpan = (trip) => Math.max(1, Math.round(Number(trip?.duration_value) || 1))

// The days a multi-day booking starting on `startDate` occupies, start date included.
export const getSpanDays = (startDate, spanDays) =>
  Array.from({ length: Math.max(1, spanDays) }, (_, offset) => dayjs(startDate).add(offset, "day"))

// A multi-day trip can start on `startDate` only when the start date passes every rule above and
// no later day of the span is past, unavailable (guide blocked or sold out) or outside the
// available date ranges (client, 9 Oct 2026: no day that can't be booked may fall inside the span).
// Blocked weekdays only limit the start day, since the trip runs straight through the span.
export const isMultiDayStartUnavailable = (trip, startDate, spanDays = getMultiDaySpan(trip)) => {
  if (isExperienceDateUnavailable(trip, startDate)) return true
  const laterDays = getSpanDays(startDate, spanDays).slice(1)
  const tripWithoutWeekdayBlocks = { ...trip, unavailable_weekdays: [] }
  return laterDays.some((day) => isExperienceDateUnavailable(tripWithoutWeekdayBlocks, day))
}

const CUT_OFF_UNITS = { minute: "minute", minutes: "minute", hour: "hour", hours: "hour", day: "day", days: "day" }

// Calendar day ("YYYY-MM-DD") and start label ("04:00 PM") of an instant in the trip's time zone,
// so every guest sees the times the guide scheduled. Falls back to the browser's zone when the
// trip has none (or an unknown one).
const getZonedParts = (instant, timeZone) => {
  const options = { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: true }
  let formatter
  try {
    formatter = new Intl.DateTimeFormat("en-US", { ...options, timeZone: timeZone || undefined })
  } catch {
    formatter = new Intl.DateTimeFormat("en-US", options)
  }
  const parts = Object.fromEntries(formatter.formatToParts(instant).map(({ type, value }) => [type, value]))
  return { dateKey: `${parts.year}-${parts.month}-${parts.day}`, label: `${parts.hour}:${parts.minute} ${String(parts.dayPeriod).toUpperCase()}` }
}

// A ticketed start time's label ("04:00 PM") in the trip's time zone.
export const getTicketedStartLabel = (trip, startsAt) => getZonedParts(dayjs(startsAt).toDate(), trip?.time_zone).label

/**
 * Bookable start times of a ticketed trip, grouped by day: { "YYYY-MM-DD": [{ id, startsAt, label }] }
 * in start order. Ticketed start times are exact instants (time_slots[].starts_at); a start time is
 * bookable until the trip's booking cut-off before it, and never on a sold-out day.
 */
export const getTicketedSlotsByDate = (trip, now = dayjs()) => {
  const cutOffUnit = CUT_OFF_UNITS[trip?.cut_off_unit]
  const bookableFrom = cutOffUnit ? dayjs(now).add(Number(trip?.cut_off_value) || 0, cutOffUnit) : dayjs(now)
  const soldOutDates = new Set((trip?.sold_out_dates || []).map((date) => String(date).slice(0, 10)))
  const byDate = {}
  ;(trip?.time_slots || [])
    .filter((slot) => slot?.starts_at && dayjs(slot.starts_at).isValid() && dayjs(slot.starts_at).isAfter(bookableFrom))
    .sort((first, second) => dayjs(first.starts_at).valueOf() - dayjs(second.starts_at).valueOf())
    .forEach((slot) => {
      const { dateKey, label } = getZonedParts(dayjs(slot.starts_at).toDate(), trip?.time_zone)
      if (soldOutDates.has(dateKey)) return
      byDate[dateKey] = [...(byDate[dateKey] || []), { id: slot.id, startsAt: slot.starts_at, label }]
    })
  return byDate
}

const DURATION_MINUTES = { minute: 1, minutes: 1, hour: 60, hours: 60 }

// End of a single-day ticketed start time ("06:00 PM"): the start plus the trip's length, in the
// trip's time zone. Null when the length is unknown.
export const getTicketedSlotEndLabel = (trip, startsAt) => {
  const minutes = Number(trip?.duration_value) * (DURATION_MINUTES[trip?.duration_unit] || 0)
  if (!startsAt || !(minutes > 0)) return null
  return getZonedParts(dayjs(startsAt).add(minutes, "minute").toDate(), trip?.time_zone).label
}

// "07:00" -> "07:00 AM"
const formatScheduleTime = (time) => {
  const [hours, minutes] = String(time).split(":").map(Number)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
  const suffix = hours >= 12 ? "PM" : "AM"
  const hours12 = hours % 12 || 12
  return `${String(hours12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${suffix}`
}

// Start times offered for a multi-day trip on `startDate`: the start time of every multi-day
// availability schedule that runs on that weekday (schedules without days run every day).
export const getMultiDayStartTimes = (trip, startDate) => {
  if (!startDate) return []
  const weekday = dayjs(startDate).day()
  const times = (trip?.availability_schedules || [])
    .filter((schedule) => schedule?.schedule_type === "multi_day" && schedule?.start_time)
    .filter((schedule) => !schedule.days?.length || schedule.days.some((day) => SCHEDULE_DAY_INDEX[String(day).toLowerCase()] === weekday))
    .map((schedule) => schedule.start_time)
  return [...new Set(times)]
    .sort()
    .map(formatScheduleTime)
    .filter(Boolean)
}
